'use client';

/**
 * Header.tsx — topplinje (Carbon UI Shell Header)
 *
 * Ren React-komponent som erstatter cds-header.
 * Fixed øverst, 48px høyde, hvit bakgrunn, flex-layout.
 *
 * Props:
 * - children: header-innhold (menu-button, name, nav, global-actions)
 * - ariaLabel: tilgjengelighet-label
 * - aiActive: når true, får header en blå gradient-bakgrunn (AI-modus)
 *
 * Når aiActive slås av, beholdes AI-stilen i AI_LINE_EXIT_MS mens den blå
 * bunnlinjen trekkes tilbake (åpningsanimasjonen baklengs).
 *
 * CSS: header.module.css
 */

import { ReactNode, useEffect, useState } from 'react';
import type { AiActivity } from '@/types/ai';
import styles from './header.module.css';

/** Varighet på den blå bunnlinjens tilbaketrekking (ms) — må samsvare med header-ai-line-undraw i CSS. */
const AI_LINE_EXIT_MS = 1000;

interface HeaderProps {
  children: ReactNode;
  ariaLabel?: string;
  /** AI-modus: blå gradient-bakgrunn (inspirert av Carbon AI Chat-launcher) */
  aiActive?: boolean;
  /** Hva AI-en gjør nå — styrer lyspuls og aura i AI-modus */
  aiActivity?: AiActivity;
}

export default function Header({
  children,
  ariaLabel = 'Hovednavigasjon',
  aiActive = false,
  aiActivity = 'idle',
}: HeaderProps) {
  // aiVisible er true fra AI slås på til tilbaketrekkingen er ferdig.
  // (Tilstandsjustering under render, som React anbefaler for å utlede
  // tilstand fra en prop.)
  const [prevAiActive, setPrevAiActive] = useState(aiActive);
  const [aiVisible, setAiVisible] = useState(aiActive);
  if (aiActive !== prevAiActive) {
    setPrevAiActive(aiActive);
    if (aiActive) setAiVisible(true);
  }
  const aiClosing = aiVisible && !aiActive;

  useEffect(() => {
    if (!aiClosing) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => setAiVisible(false), reduceMotion ? 0 : AI_LINE_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [aiClosing]);

  return (
    <header
      className={aiVisible ? `${styles.header} ${styles.headerAiActive}` : styles.header}
      aria-label={ariaLabel}
      data-ai-active={aiActive || undefined}
      data-ai-closing={aiClosing || undefined}
      data-ai-activity={aiActive ? aiActivity : undefined}
    >
      {children}
      {aiVisible && <span className={styles.aiLight} aria-hidden="true" />}
    </header>
  );
}
